/**
 * v3.15-1179 — Homepage rebuilt to user-locked wireframe.
 *
 * Foundation:
 *   PTC Client Hub + EasyQuickFund™ + CreditForward360™
 *   Partner Financial Infrastructure
 *   Need funding solutions for your clients?
 *
 * Direction:
 *   - Hub screenshots carry the visual weight, not typography.
 *   - Alternating dark / light sections.
 *   - Copy is the editorial source of truth (verbatim).
 *   - Login is a small utility near the bottom.
 *   - Ecosystem trio (EasyQuickFund / Business Funding / CreditForward360)
 *     is represented equally. Ghost is a product, not a mascot.
 *
 * Section order (locked):
 *   1.  Hero (dark)                  — eyebrow + headline + lead + CTA + hub-dashboard.jpg
 *   2.  Prestigious Hub (light)      — the platform that connects all products
 *   3.  EasyQuickFund™ (dark)        — consumer funding
 *   4.  Business Funding (light)     — business funding
 *   5.  Differentiator pivot (dark)  — "Most companies stop. We don't."
 *   6.  CreditForward360™ (light)    — recovery product
 *   7.  Ghost™ (dark)                — operations officer product
 *   8.  Integrated ecosystem (light) — verbatim statement
 *   9.  What partners get (dark)     — 8 capability bullets
 *   10. Partnership inquiries (light)— support email + brand domains
 *   11. Partner sign-in (dark, slim) — utility
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowDown, Loader2, AlertCircle, ExternalLink,
  ShieldCheck, Eye, Headphones, Banknote, Building2, RefreshCw,
  Activity, Users as UsersIcon, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PublicLayout from '../components/PublicLayout';
import { getApiErrorMessage } from '../lib/errorUtils';

// ─────────────────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────────────────

const SectionEyebrow = ({ children, tone = 'gold', testId }) => (
  <p
    data-testid={testId}
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase ${
      tone === 'gold' ? 'text-gold' : 'text-slate-500'
    }`}
  >
    {children}
  </p>
);

// Browser-style frame around a Hub screenshot so the software feels real.
const ScreenFrame = ({ src, alt, testId }) => (
  <div
    data-testid={testId}
    className="relative rounded-xl overflow-hidden border border-white/10 bg-slate-900 shadow-2xl ring-1 ring-gold/15"
  >
    <div className="flex items-center gap-1.5 px-4 h-9 bg-slate-900/80 border-b border-white/5">
      <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-700" />
      <span className="ml-4 text-[10px] tracking-widest text-slate-500 uppercase">
        hub.prestigiouscompanies.org
      </span>
    </div>
    <img
      src={src}
      alt={alt}
      className="block w-full h-auto"
      loading="lazy"
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// 1. HERO — DARK
// ─────────────────────────────────────────────────────────────────────────
const HeroSection = () => (
  <section
    data-testid="home-hero"
    className="relative bg-slate-950 text-white overflow-hidden"
  >
    <div aria-hidden className="absolute -top-32 -left-32 w-[700px] h-[700px] bg-gold/[0.04] rounded-full blur-3xl pointer-events-none" />
    <div aria-hidden className="absolute -bottom-40 right-0 w-[600px] h-[600px] bg-primary/20 rounded-full blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* COPY — 5 cols (40%) */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <SectionEyebrow testId="hero-eyebrow">
            PTC Client Hub  ·  EasyQuickFund™  ·  CreditForward360™
          </SectionEyebrow>
          <h1
            data-testid="hero-h1"
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.05] font-semibold text-white"
          >
            Partner Financial Infrastructure
          </h1>
          <p
            data-testid="hero-lead-question"
            className="mt-6 text-lg lg:text-xl text-slate-300 italic"
          >
            Need funding solutions for your clients?
          </p>
          <p
            data-testid="hero-lead-paragraph"
            className="mt-6 text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl"
          >
            Prestigious Companies gives approved partners access to a fully
            integrated financial infrastructure designed to help you close
            more business — not just submit applications.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/presentation"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-gold text-slate-950 font-semibold text-sm px-6 py-3 rounded hover:bg-gold/90 transition-colors"
            >
              Request Partner Access <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="#partner-login"
              data-testid="hero-cta-secondary"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors inline-flex items-center gap-1.5"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('partner-login')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Partner Login <ArrowDown className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* SCREENSHOT — 7 cols (60%), dominant */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <ScreenFrame
            testId="hero-screenshot"
            src="/marketing/hub-dashboard.jpg"
            alt="Prestigious Hub — partner dashboard"
          />
        </div>
      </div>
    </div>

    {/* Hairline ecosystem strip — equal weighting of the three core products */}
    <div className="border-t border-white/5 bg-slate-950/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-12 flex items-center justify-center gap-6 lg:gap-12 text-[11px] tracking-[0.22em] uppercase text-slate-500">
        <span data-testid="ecosystem-strip-eqf">EasyQuickFund™</span>
        <span aria-hidden className="h-1 w-1 rounded-full bg-slate-700" />
        <span data-testid="ecosystem-strip-bf">Business Funding</span>
        <span aria-hidden className="h-1 w-1 rounded-full bg-slate-700" />
        <span data-testid="ecosystem-strip-cf360">CreditForward360™</span>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 2. PRESTIGIOUS HUB — LIGHT
// ─────────────────────────────────────────────────────────────────────────
const HubSection = () => (
  <section
    data-testid="home-hub"
    className="bg-white text-slate-900"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* COPY — 5 cols */}
        <div className="lg:col-span-5">
          <SectionEyebrow>Prestigious Hub</SectionEyebrow>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
            The platform that connects every product.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
            Every client enters Prestigious Hub, where our dedicated
            Application Concierge manages the process from submission
            through funding while keeping you informed with real-time
            visibility every step of the way.
          </p>

          {/* Hierarchy block — locked per user spec */}
          <div
            data-testid="hub-hierarchy"
            className="mt-10 border border-slate-200 rounded-lg bg-slate-50/60 p-6"
          >
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-slate-500">
              How the ecosystem connects
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold" />
                <span><span className="font-semibold text-slate-900">Prestigious Companies</span> — the parent organization</span>
              </li>
              <li className="flex items-start gap-3 pl-5">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold" />
                <span><span className="font-semibold text-slate-900">Prestigious Hub</span> — the platform that connects every product</span>
              </li>
              <li className="pl-10">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-slate-700">
                  <span>• EasyQuickFund™</span>
                  <span>• Business Funding</span>
                  <span>• CreditForward360™</span>
                  <span>• Ghost™</span>
                </div>
              </li>
              <li className="flex items-start gap-3 pl-5">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold" />
                <span><span className="font-semibold text-slate-900">Dedicated Application Concierge</span> — manages every file end-to-end</span>
              </li>
            </ul>
          </div>
        </div>

        {/* SCREENSHOT — 7 cols */}
        <div className="lg:col-span-7">
          <ScreenFrame
            testId="hub-screenshot"
            src="/marketing/hub-activity.jpg"
            alt="Prestigious Hub — real-time activity feed"
          />
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 3. EASYQUICKFUND — DARK
// ─────────────────────────────────────────────────────────────────────────
const EasyQuickFundSection = () => (
  <section
    data-testid="home-eqf"
    className="bg-slate-950 text-white"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* SCREENSHOT — 7 cols, left */}
        <div className="lg:col-span-7 order-1">
          <ScreenFrame
            testId="eqf-screenshot"
            src="/marketing/hub-clients.jpg"
            alt="EasyQuickFund — consumer funding intake inside the Hub"
          />
        </div>

        {/* COPY — 5 cols, right */}
        <div className="lg:col-span-5 order-2">
          <SectionEyebrow>EasyQuickFund™  ·  Consumer Funding</SectionEyebrow>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-white">
            One submission. Multiple funding paths.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-slate-300 leading-relaxed">
            Consumer funding is handled through EasyQuickFund™, giving your
            clients access to multiple funding opportunities through one
            simple submission while our team manages the entire process
            for you.
          </p>
          <a
            href="https://easyquickfund.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="eqf-external-link"
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-gold/80 transition-colors"
          >
            Visit easyquickfund.com <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 4. BUSINESS FUNDING — LIGHT  (restored, equal-weight to EQF and CF360)
// ─────────────────────────────────────────────────────────────────────────
const BusinessFundingSection = () => (
  <section
    data-testid="home-business-funding"
    className="bg-slate-50 text-slate-900"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* COPY — 5 cols */}
        <div className="lg:col-span-5">
          <SectionEyebrow>Business Funding</SectionEyebrow>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
            Capital for the businesses your clients run.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
            Business Funding sits inside the same Prestigious Hub workflow
            as consumer funding — a parallel path for partners whose clients
            need working capital, equipment financing, or growth funding for
            their company.
          </p>
          <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed">
            Same Concierge. Same visibility. Same recovery safety net if the
            initial answer isn&apos;t yes.
          </p>
        </div>

        {/* SCREENSHOT — 7 cols */}
        <div className="lg:col-span-7">
          <ScreenFrame
            testId="business-funding-screenshot"
            src="/marketing/hero-enterprise.jpg"
            alt="Business Funding — capital paths inside Prestigious Hub"
          />
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 5. THE DIFFERENTIATOR — DARK, centered pivot
// ─────────────────────────────────────────────────────────────────────────
const DifferentiatorSection = () => (
  <section
    data-testid="home-differentiator"
    className="bg-slate-950 text-white"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-28 lg:py-36 text-center">
      <SectionEyebrow>What separates us</SectionEyebrow>
      <p
        data-testid="differentiator-lead"
        className="mt-6 font-heading text-2xl sm:text-3xl lg:text-[2.25rem] leading-snug text-white"
      >
        But what truly separates us from everyone else is CreditForward360™.
      </p>
      <div className="mt-14 space-y-6">
        <p className="text-xl lg:text-2xl text-slate-300">
          Most funding companies stop working when a client doesn&apos;t qualify.
        </p>
        <p className="text-xl lg:text-2xl text-gold font-semibold">
          We don&apos;t.
        </p>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 6. CREDITFORWARD360 — LIGHT  (full product treatment)
// ─────────────────────────────────────────────────────────────────────────
const CreditForward360Section = () => (
  <section
    data-testid="home-cf360"
    className="bg-white text-slate-900"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* COPY — 5 cols */}
        <div className="lg:col-span-5">
          <SectionEyebrow>CreditForward360™  ·  Recovery Product</SectionEyebrow>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
            When the answer isn&apos;t yes — the work continues.
          </h2>

          <div className="mt-6 space-y-5 text-base lg:text-lg text-slate-600 leading-relaxed">
            <p>
              If EasyQuickFund or Business Funding cannot fund the client
              today, CreditForward360 automatically becomes the next product
              in the customer&apos;s journey.
            </p>
            <p>
              Using our proprietary credit improvement workflow and proven
              financial recovery strategies, we work directly with your
              client to strengthen their credit profile while keeping
              everything connected inside Prestigious Hub.
            </p>
            <p>
              When they become fundable, they move back into the funding
              process — without starting over.
            </p>
          </div>

          {/* 3-line gold callout */}
          <div
            data-testid="cf360-callout"
            className="mt-10 border-l-2 border-gold pl-6 py-2 space-y-1.5"
          >
            <p data-testid="cf360-line-1" className="text-base lg:text-lg font-semibold text-slate-900">You don&apos;t lose the client.</p>
            <p data-testid="cf360-line-2" className="text-base lg:text-lg font-semibold text-slate-900">You don&apos;t lose the relationship.</p>
            <p data-testid="cf360-line-3" className="text-base lg:text-lg font-semibold text-slate-900">You simply gain another opportunity to close the deal.</p>
          </div>

          <a
            href="https://creditforward360.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cf360-external-link"
            className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Visit creditforward360.com <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* SCREENSHOT — 7 cols */}
        <div className="lg:col-span-7">
          <ScreenFrame
            testId="cf360-screenshot"
            src="/marketing/hub-activity.jpg"
            alt="CreditForward360 — recovery workflow inside the Hub"
          />
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 7. GHOST — DARK (product treatment, not mascot)
// ─────────────────────────────────────────────────────────────────────────
const GhostSection = () => (
  <section
    data-testid="home-ghost"
    className="bg-slate-950 text-white"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* MARK — 5 cols, left */}
        <div className="lg:col-span-5 flex justify-center lg:justify-start">
          <div className="relative">
            <div aria-hidden className="absolute inset-0 bg-gold/10 blur-3xl rounded-full" />
            <img
              src="/marketing/ghost-mark.png"
              alt="Ghost — operations officer"
              data-testid="ghost-mark"
              className="relative w-56 h-56 lg:w-72 lg:h-72 object-contain"
              loading="lazy"
            />
          </div>
        </div>

        {/* COPY — 7 cols, right */}
        <div className="lg:col-span-7">
          <SectionEyebrow>Ghost™  ·  Operations Product</SectionEyebrow>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-white">
            The operations officer that never sleeps.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Ghost is a product inside the Prestigious ecosystem — quiet
            intelligence that supports every other product in the Hub.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm text-slate-300">
            <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Supports every product</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Supports every Concierge</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Supports every client</li>
            <li className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Supports every partner</li>
          </ul>

          <Link
            to="/ghost"
            data-testid="ghost-learn-more"
            className="mt-10 inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:text-gold/80 transition-colors"
          >
            Learn about Ghost <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 8. INTEGRATED ECOSYSTEM — LIGHT, centered statement
// ─────────────────────────────────────────────────────────────────────────
const EcosystemStatementSection = () => (
  <section
    data-testid="home-ecosystem-statement"
    className="bg-slate-50 text-slate-900"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-28 lg:py-36 text-center">
      <SectionEyebrow>One integrated ecosystem</SectionEyebrow>
      <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
        Everything connected. Through one platform.
      </h2>
      <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed">
        Our integrated ecosystem keeps every conversation, every document,
        every funding opportunity, and every recovery plan connected
        through one platform — creating a better experience for your
        clients and a more profitable business for you.
      </p>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 9. WHAT PARTNERS GET — DARK
// ─────────────────────────────────────────────────────────────────────────
const CAPABILITIES = [
  { icon: ShieldCheck,  label: 'Secure Partner Access',            blurb: 'Approved-partner-only access to the full ecosystem.' },
  { icon: Eye,          label: 'Real-Time Deal Visibility',        blurb: 'Track every file from submission through funding.' },
  { icon: Headphones,   label: 'Dedicated Application Concierge',  blurb: 'A human owner managing each client end-to-end.' },
  { icon: Banknote,     label: 'Consumer Funding (EasyQuickFund™)',blurb: 'Multiple funding paths from one submission.' },
  { icon: Building2,    label: 'Business Funding Solutions',       blurb: 'Working capital, equipment and growth financing.' },
  { icon: RefreshCw,    label: 'CreditForward360™ Recovery Program', blurb: 'When the answer is not yes today — yet.' },
  { icon: Activity,     label: 'Ghost™ Operations Officer Monitoring', blurb: 'Always-on intelligence across every file.' },
  { icon: UsersIcon,    label: 'Internal Funding Specialists',     blurb: 'In-house lenders and underwriters on every deal.' },
];

const PartnerCapabilitiesSection = () => (
  <section
    data-testid="home-capabilities"
    className="bg-slate-950 text-white"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="max-w-3xl">
        <SectionEyebrow>Available exclusively to approved partner organizations</SectionEyebrow>
        <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-white">
          What partners get.
        </h2>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 gap-x-12 gap-y-10">
        {CAPABILITIES.map((c, i) => (
          <div
            key={c.label}
            data-testid={`capability-${i + 1}`}
            className="flex items-start gap-4"
          >
            <div className="shrink-0 h-10 w-10 rounded-md border border-gold/30 bg-gold/5 flex items-center justify-center">
              <c.icon className="h-5 w-5 text-gold" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">{c.label}</h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{c.blurb}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 10. PARTNERSHIP INQUIRIES — LIGHT
// ─────────────────────────────────────────────────────────────────────────
const PartnershipInquiriesSection = () => (
  <section
    data-testid="home-inquiries"
    className="bg-white text-slate-900"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
      <SectionEyebrow>Partnership Inquiries</SectionEyebrow>
      <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold leading-tight text-slate-900">
        Become a partner.
      </h2>

      <a
        href="mailto:support@prestigiouscompanies.org"
        data-testid="inquiries-email"
        className="mt-10 inline-block font-heading text-2xl sm:text-3xl text-primary hover:text-gold transition-colors"
      >
        support@prestigiouscompanies.org
      </a>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-medium text-slate-500">
        <a href="https://easyquickfund.com"   target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-1" data-testid="inquiries-link-eqf">easyquickfund.com <ExternalLink className="h-3 w-3" /></a>
        <a href="https://creditforward360.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-1" data-testid="inquiries-link-cf360">creditforward360.com <ExternalLink className="h-3 w-3" /></a>
        <a href="https://hub.prestigiouscompanies.org"   target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-1" data-testid="inquiries-link-hub">hub.prestigiouscompanies.org <ExternalLink className="h-3 w-3" /></a>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────
// 11. PARTNER SIGN-IN — DARK, slim utility
// ─────────────────────────────────────────────────────────────────────────
const PartnerSignInSection = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Sign in failed. Please try again.');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="partner-login"
      data-testid="home-partner-login"
      className="bg-slate-950 text-white border-t border-white/5"
    >
      <div className="max-w-md mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="text-center">
          <SectionEyebrow>Approved Partners</SectionEyebrow>
          <h3 className="mt-3 font-heading text-xl sm:text-2xl font-semibold text-white">
            Sign in to Prestigious Hub
          </h3>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-8 space-y-4"
          data-testid="partner-login-form"
        >
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-300 bg-red-950/40 border border-red-900/50 rounded px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span data-testid="partner-login-error">{error}</span>
            </div>
          )}
          <div>
            <Label htmlFor="partner-email" className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
            <Input
              id="partner-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="partner-login-email"
              className="mt-1.5 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600 focus-visible:ring-gold/30"
              placeholder="you@partner.com"
            />
          </div>
          <div>
            <Label htmlFor="partner-password" className="text-xs uppercase tracking-wider text-slate-400">Password</Label>
            <Input
              id="partner-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="partner-login-password"
              className="mt-1.5 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600 focus-visible:ring-gold/30"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="partner-login-submit"
            className="w-full bg-gold text-slate-950 hover:bg-gold/90 font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
          </Button>
          <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
            <Link to="/forgot-password" className="hover:text-slate-300 transition-colors" data-testid="partner-login-forgot">Forgot password</Link>
            <Link to="/presentation" className="hover:text-slate-300 transition-colors" data-testid="partner-login-request-access">Request partner access</Link>
          </div>
        </form>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────
const HomePage = () => (
  <PublicLayout testId="home-page">
    <HeroSection />
    <HubSection />
    <EasyQuickFundSection />
    <BusinessFundingSection />
    <DifferentiatorSection />
    <CreditForward360Section />
    <GhostSection />
    <EcosystemStatementSection />
    <PartnerCapabilitiesSection />
    <PartnershipInquiriesSection />
    <PartnerSignInSection />
  </PublicLayout>
);

export default HomePage;
